import OdometerIcon from "../../../../../pages/home/components/icons/Odometer";
import SpeedIcon from "../../../../../pages/home/components/icons/Speed";
import type { VehicleSocket } from "../../../../../store/admin/vehiclesSocketStore";
import IgnitionIcon from "../../../icons/biggerIcons/IgnitionIcon";
import { useTranslation } from "react-i18next";
import LastReportIcon from "../../../icons/biggerIcons/LastReportIcon";
import { getTimeAgo, type TimeTranslations } from "../../../../../utils/getDate";
import { useMemo } from "react";
import { useVehiclesSocketStore } from "../../../../../store/admin/vehiclesSocketStore";

interface GeneralInformationContentProps {
  vehicle: VehicleSocket;
  isDarkMode: boolean;
}

const formatParamKey = (key: string): string =>
  key
    .replace(/[_/]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

// Componente de loader para el address
const AddressLoader = ({ isDarkMode, loadingText }: { isDarkMode: boolean; loadingText: string }) => (
  <div className="flex items-center gap-2">
    <div className={`animate-spin h-3 w-3 border-2 border-t-transparent rounded-full ${isDarkMode ? 'border-gray-400' : 'border-gray-500'}`}></div>
    <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-xs`}>
      {loadingText}
    </span>
  </div>
);

export default function GeneralInformationContent({
  vehicle: initialVehicle,
  isDarkMode,
}: GeneralInformationContentProps) {
  const textColor = isDarkMode ? "text-gray-300" : "text-gray-700";
  const labelColor = isDarkMode ? "text-gray-500" : "text-gray-500";
  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";

  const { t } = useTranslation("lite");

  const timeTranslations: TimeTranslations = useMemo(() => ({
    noData: t("time.noData"),
    rightNow: t("time.rightNow"),
    min: t("time.min"),
    hour: t("time.hour"),
    hours: t("time.hours"),
    day: t("time.day"),
    days: t("time.days"),
    month: t("time.month"),
    months: t("time.months"),
    and: t("time.and"),
  }), [t]);

  // Obtener el vehículo actualizado del store - O(1) via Map
  const vehicleFromStore = useVehiclesSocketStore(
    (state) => {
      const idx = state.vehiclesMap.get(initialVehicle?.id);
      return idx !== undefined ? state.vehicles[idx] : undefined;
    }
  );

  // Verificar si se está cargando el address de este vehículo (O(1) lookup)
  const isLoadingAddress = useVehiclesSocketStore(
    (state) => !!state.loadingAddressDeviceIds[initialVehicle?.id]
  );

  // Usar el vehículo del store si está disponible (datos frescos del socket)
  const vehicle = vehicleFromStore || initialVehicle;

  return (
    <div className={`p-3 ${borderColor}`}>
      <div className="border-y border-gray-500 pb-3 text-xs">
        <div className="flex items-center justify-between mt-2">
          <h4 className={`${textColor} font-semibold text-sm mb-2`}>
            {t("sidebar.information")}
          </h4>
          <div className="flex items-center gap-2 text-sm">
            <LastReportIcon color="#9CA3AF" />
            <p className={`${textColor} font-medium text-xs`}>
              {t("time.ago")}: {getTimeAgo(vehicle.lastReport._seconds, timeTranslations)}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="col-span-2">
            <span className={labelColor}>{t("sidebar.address")}</span>
            {isLoadingAddress ? (
              <AddressLoader isDarkMode={isDarkMode} loadingText={t("generalInfo.loadingAddress")} />
            ) : (
              <p className={`${textColor} font-medium`}>
                {vehicle.address || t("generalInfo.noAddressAvailable")}
              </p>
            )}
          </div>
          <div className="col-span-2">
            <span className={labelColor}>{t("sidebar.reference")}</span>
            <p className={`${textColor} font-medium`}>
              {vehicle.reference || "N/A"}
            </p>
          </div>

          <div className="flex col-span-2 flex-col">
            <span className={labelColor}>{t("sidebar.coordinates")}</span>
            <p className={`${textColor} font-medium`}>{vehicle.latitude.toFixed(6)}, {vehicle.longitude.toFixed(6)}</p>
          </div>
        </div>
      </div>
      <div className="flex col-span-2 items-center justify-center gap-10 py-5 border-b border-gray-500">
        <div className="flex flex-col items-center text-center ">
          <div className={`border-b-2 border-gray-500 pb-2 transition-colors`}>
            <OdometerIcon color="#9CA3AF" />
          </div>
          <span className="text-gray-500 text-xs mt-2">
            {t("sidebar.odometer")}
          </span>
          <span className={`${textColor} text-xs font-bold`}>
            {vehicle.odometer != null ? `${Math.round(vehicle.odometer).toLocaleString()} km` : t("vehicleCard.noData")}
          </span>
        </div>
        <div className="flex flex-col items-center text-center ">
          <div className={`border-b-2 border-gray-500 pb-2 transition-colors`}>
            <SpeedIcon color="#9CA3AF" className="w-5 h-5" />
          </div>
          <span className="text-gray-500 text-xs mt-2">
            {t("sidebar.speed")}
          </span>
          <span className={`${textColor} text-xs font-bold`}>
            {vehicle.speed != null ? `${Math.round(vehicle.speed)} km/h` : t("vehicleCard.noData")}
          </span>
        </div>

        <div>
          <div className="flex flex-col items-center flex-1 min-w-0 cursor-pointer">
            <div
              className={`border-b-2 border-gray-500 pb-2 transition-colors`}
            >
              <IgnitionIcon color="#9CA3AF" />
            </div>
            <span className="text-gray-500 mt-2 text-xs">
              {t("sidebar.ignition")}
            </span>
            <span className={`text-xs font-bold ${textColor}`}>
              {vehicle.ignition ? t("sidebar.on") : t("sidebar.off")}
            </span>
          </div>
        </div>
      </div>
      <div className="flex justify-between gap-2 col-span-2 py-3 border-b border-gray-500 text-xs">
        <div className="flex flex-col w-1/2">
          <span className={labelColor}>{t("sidebar.deviceType")}</span>
          <p className={`${textColor} font-medium`}>{vehicle.device.type}</p>
        </div>
        <div className="flex flex-col w-1/2">
          <span className={labelColor}>{t("sidebar.deviceId")}</span>
          <p className={`${textColor} font-medium`}>{vehicle.device.id}</p>
        </div>
      </div>
      <div className="mt-3 text-xs">
        <div className="flex items-center gap-1.5 mb-2">
          <i className="ri-pulse-line text-sm text-gray-400"></i>
          <h4 className={`${textColor} font-semibold text-sm`}>
            {t("sidebar.parameters")}
          </h4>
          <span className={`ml-auto px-1.5 py-0.5 rounded-full text-[9px] font-bold ${isDarkMode ? "bg-primary-500/15 text-primary-400" : "bg-primary-500/10 text-primary-600"}`}>
            {4 + Object.keys(vehicle.socketParams ?? {}).length}
          </span>
        </div>
        <div className={`max-h-56 overflow-y-auto pr-1 grid grid-cols-2 gap-1.5 rounded-lg p-1.5 ${isDarkMode ? "bg-black/20" : "bg-gray-50"}`}>
          {[
            { key: t("sidebar.datetime"), value: vehicle.params.datetime ? new Date(vehicle.params.datetime).toLocaleString() : t("vehicleCard.noData") },
            { key: t("sidebar.engineStatus"), value: vehicle.params.engineStatus || t("vehicleCard.noData") },
            { key: t("sidebar.imei"), value: vehicle.imei || "N/A" },
            { key: t("sidebar.latitude"), value: (vehicle.latitude ?? vehicle.params.latitude)?.toFixed(6) ?? "N/A" },
            ...Object.entries(vehicle.socketParams ?? {})
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([k, v]) => ({ key: formatParamKey(k), value: v })),
          ].map((item, index) => (
            <div
              key={`${item.key}-${index}`}
              className={`flex flex-col gap-0.5 px-2 py-1 rounded-md border ${isDarkMode ? "bg-dark-card/50 border-dark-border" : "bg-white border-gray-200"}`}
              title={`${item.key}: ${item.value}`}
            >
              <span className={`truncate uppercase tracking-wide text-[9px] leading-none ${labelColor}`}>
                {item.key}
              </span>
              <span className={`font-mono font-semibold text-[11px] truncate ${textColor}`}>
                {item.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
